import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useCounterTrends, CounterTrendDataPoint } from '../hooks/useCounterTrends';
import { HabitLog } from '../../types';
import { useTheme } from '../hooks/useTheme';

interface CounterTrendChartProps {
  logs: HabitLog[];
  habitId: string;
  dailyTarget: number;
  language: 'en' | 'ar';
}

const CustomTooltip = ({ active, payload, label, language, palette }: any) => {
  if (!active || !payload || !payload.length) return null;

  const dataPoint = payload[0]?.payload as CounterTrendDataPoint | undefined;
  if (!dataPoint) return null;

  const isAboveTarget = dataPoint.value >= dataPoint.target;
  // Hitting the target is the best tier; falling short is the onTime highlight.
  const valueColor = isAboveTarget ? palette.takbirah.bright : palette.onTime.bright;

  return (
    <div className="bg-surface/95 backdrop-blur-sm rounded-lg p-3 shadow-xl border border-separator">
      <p className="text-xs text-gray-400 mb-2 font-medium">{label}</p>
      <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wider">
        {language === 'ar' ? 'متوسط 7 أيام' : '7-Day Average'}
      </p>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: valueColor }}
          />
          <span className="text-gray-300 flex-1">
            {language === 'ar' ? 'القيمة' : 'Value'}
          </span>
          <span 
            className="font-bold tabular-nums" 
            style={{ color: valueColor }}
          >
            {dataPoint.value.toFixed(1)}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full bg-blue-400" />
          <span className="text-gray-300 flex-1">
            {language === 'ar' ? 'الهدف' : 'Target'}
          </span>
          <span className="font-bold tabular-nums text-blue-400">
            {dataPoint.target}
          </span>
        </div>
        <div className="text-[10px] text-gray-500 mt-1">
          {language === 'ar' ? 'يوم واحد: ' : 'Single Day: '}
          <span className="text-white font-medium">{dataPoint.rawValue}</span>
        </div>
      </div>
    </div>
  );
};

const CounterTrendChart: React.FC<CounterTrendChartProps> = ({ 
  logs, 
  habitId, 
  dailyTarget, 
  language 
}) => {
  const data = useCounterTrends(logs, habitId, dailyTarget);
  const { palette } = useTheme();

  const tickInterval = Math.max(1, Math.floor(data.length / 12));
  
  // Calculate Y-axis domain to accommodate both value and target
  const maxValue = Math.max(...data.map(d => d.value), dailyTarget);
  const yAxisMax = Math.ceil(maxValue * 1.2); // 20% padding

  return (
    <div className="w-full">
      <div className="w-full h-40 -mx-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={palette.takbirah.bright} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={palette.takbirah.bright} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 9, fill: palette.text3 }}
              tickLine={false}
              axisLine={false}
              interval={tickInterval}
            />
            <YAxis
              domain={[0, yAxisMax]}
              tick={{ fontSize: 9, fill: palette.text3 }}
              tickLine={false}
              axisLine={false}
              width={25}
            />
            <Tooltip content={<CustomTooltip language={language} palette={palette} />} />
            
            {/* Target Reference Line */}
            <ReferenceLine 
              y={dailyTarget} 
              stroke={palette.jamaa.bright}
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: language === 'ar' ? 'الهدف' : 'Target',
                position: 'right',
                fill: palette.jamaa.bright,
                fontSize: 10,
              }}
            />
            
            {/* Value Area */}
            <Area
              type="monotone"
              dataKey="value"
              stroke={palette.takbirah.bright}
              strokeWidth={2}
              fill="url(#colorValue)"
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CounterTrendChart;
